import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { SettingsLayout } from "@/components/settings-layout";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthGuard, GuestGuard } from "@/components/auth-guard";
import { AppLayout } from "@/components/app-layout";
import { ProjectRedirect } from "@/components/project-redirect";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";

const LoginPage = lazy(() => import("@/pages/auth/login"));
const RegisterPage = lazy(() => import("@/pages/auth/register"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/reset-password"));
const VerifyEmailPage = lazy(() => import("@/pages/auth/verify-email"));
const AcceptInvitePage = lazy(() => import("@/pages/auth/accept-invite"));
const GoogleCallbackPage = lazy(() => import("@/pages/auth/google-callback"));
const MonitorsPage = lazy(() => import("@/pages/monitors"));
const CreateMonitorPage = lazy(() => import("@/pages/monitors/create"));
const EditMonitorPage = lazy(() => import("@/pages/monitors/edit"));
const MonitorViewPage = lazy(() => import("@/pages/monitors/view"));
const CreateIncidentPage = lazy(
  () => import("@/pages/monitors/incidents/create"),
);
const IncidentViewPage = lazy(() => import("@/pages/monitors/incidents/view"));
const EditIncidentPage = lazy(() => import("@/pages/monitors/incidents/edit"));
const IncidentsPage = lazy(() => import("@/pages/incidents"));
const StatusPagesPage = lazy(() => import("@/pages/status-pages"));
const CreateStatusPagePage = lazy(() => import("@/pages/status-pages/create"));
const EditStatusPageLayout = lazy(
  () => import("@/pages/status-pages/edit/index"),
);
const EditSettingsTab = lazy(
  () => import("@/pages/status-pages/edit/settings"),
);
const EditComponentsTab = lazy(
  () => import("@/pages/status-pages/edit/components"),
);
const EditStatusUpdatesTab = lazy(
  () => import("@/pages/status-pages/edit/status-updates"),
);
const EditMaintenanceTab = lazy(
  () => import("@/pages/status-pages/edit/maintenance"),
);
const EditSubscribersTab = lazy(
  () => import("@/pages/status-pages/edit/subscribers"),
);
const EditTranslationsTab = lazy(
  () => import("@/pages/status-pages/edit/translations"),
);
const MembersPage = lazy(() => import("@/pages/settings/members"));
const ProfilePage = lazy(() => import("@/pages/settings/profile"));
const IntegrationsPage = lazy(() => import("@/pages/settings/integrations"));
const BillingPage = lazy(() => import("@/pages/settings/billing"));
const ApiTokensPage = lazy(() => import("@/pages/settings/api-tokens"));
const ProjectSettingsPage = lazy(() => import("@/pages/settings/project"));
const OrganizationSettingsPage = lazy(
  () => import("@/pages/settings/organization"),
);
const HeartbeatsPage = lazy(() => import("@/pages/heartbeats"));
const CreateHeartbeatPage = lazy(() => import("@/pages/heartbeats/create"));
const HeartbeatViewPage = lazy(() => import("@/pages/heartbeats/view"));
const EditHeartbeatPage = lazy(() => import("@/pages/heartbeats/edit"));
const OnboardingPage = lazy(() => import("@/pages/onboarding"));

const queryClient = new QueryClient();

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Suspense>
              <Routes>
                <Route
                  path="/auth/login"
                  element={
                    <GuestGuard>
                      <LoginPage />
                    </GuestGuard>
                  }
                />
                <Route
                  path="/auth/register"
                  element={
                    <GuestGuard>
                      <RegisterPage />
                    </GuestGuard>
                  }
                />
                <Route
                  path="/auth/forgot-password"
                  element={
                    <GuestGuard>
                      <ForgotPasswordPage />
                    </GuestGuard>
                  }
                />
                <Route
                  path="/auth/reset-password"
                  element={
                    <GuestGuard>
                      <ResetPasswordPage />
                    </GuestGuard>
                  }
                />
                <Route
                  path="/auth/verify-email"
                  element={<VerifyEmailPage />}
                />
                <Route
                  path="/auth/accept-invite"
                  element={
                    <GuestGuard>
                      <AcceptInvitePage />
                    </GuestGuard>
                  }
                />
                <Route
                  path="/auth/google/callback"
                  element={
                    <GuestGuard>
                      <GoogleCallbackPage />
                    </GuestGuard>
                  }
                />
                <Route
                  path="/onboarding"
                  element={
                    <AuthGuard>
                      <OnboardingPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/"
                  element={
                    <AuthGuard>
                      <ProjectRedirect />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/monitors/new"
                  element={
                    <AuthGuard>
                      <CreateMonitorPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/heartbeats/new"
                  element={
                    <AuthGuard>
                      <CreateHeartbeatPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/status-pages/new"
                  element={
                    <AuthGuard>
                      <CreateStatusPagePage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/incidents/new"
                  element={
                    <AuthGuard>
                      <CreateIncidentPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/monitors/:id/incidents/new"
                  element={
                    <AuthGuard>
                      <CreateIncidentPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/monitors/:id/edit"
                  element={
                    <AuthGuard>
                      <EditMonitorPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/incidents/:incidentId/edit"
                  element={
                    <AuthGuard>
                      <EditIncidentPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/projects/:projectId/status-pages/:id/edit"
                  element={
                    <AuthGuard>
                      <EditStatusPageLayout />
                    </AuthGuard>
                  }
                >
                  <Route index element={<Navigate to="settings" replace />} />
                  <Route path="settings" element={<EditSettingsTab />} />
                  <Route path="components" element={<EditComponentsTab />} />
                  <Route
                    path="status-updates"
                    element={<EditStatusUpdatesTab />}
                  />
                  <Route path="maintenance" element={<EditMaintenanceTab />} />
                  <Route path="subscribers" element={<EditSubscribersTab />} />
                  <Route
                    path="translations"
                    element={<EditTranslationsTab />}
                  />
                </Route>
                <Route
                  path="/projects/:projectId/heartbeats/:id/edit"
                  element={
                    <AuthGuard>
                      <EditHeartbeatPage />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <AuthGuard>
                      <AppLayout />
                    </AuthGuard>
                  }
                >
                  <Route index element={<ProfilePage />} />
                </Route>
                <Route
                  path="/settings"
                  element={
                    <AuthGuard>
                      <AppLayout />
                    </AuthGuard>
                  }
                >
                  <Route element={<SettingsLayout />}>
                    <Route index element={<Navigate to="members" replace />} />
                    <Route path="members" element={<MembersPage />} />
                    <Route
                      path="organization"
                      element={<OrganizationSettingsPage />}
                    />
                    <Route path="integrations" element={<IntegrationsPage />} />
                    <Route path="api-tokens" element={<ApiTokensPage />} />
                    <Route path="billing" element={<BillingPage />} />
                  </Route>
                </Route>
                <Route
                  path="/projects/:projectId"
                  element={
                    <AuthGuard>
                      <AppLayout />
                    </AuthGuard>
                  }
                >
                  <Route index element={<MonitorsPage />} />
                  <Route path="incidents" element={<IncidentsPage />} />
                  <Route
                    path="incidents/:incidentId"
                    element={<IncidentViewPage />}
                  />
                  <Route path="monitors" element={<MonitorsPage />} />
                  <Route path="monitors/:id" element={<MonitorViewPage />} />
                  <Route path="heartbeats" element={<HeartbeatsPage />} />
                  <Route
                    path="heartbeats/:id"
                    element={<HeartbeatViewPage />}
                  />
                  <Route path="status-pages" element={<StatusPagesPage />} />
                  <Route
                    path="project-settings"
                    element={<ProjectSettingsPage />}
                  />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster />
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

function SentryFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="text-muted-foreground mt-2">
          An unexpected error occurred. Our team has been notified.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
